import { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

const PAIRS = [
  { before: "/images/sauna-before.jpg", after: "/images/sauna-pool.webp", label: "Fasssauna", note: "Herstellerbild → KI-Ambientebild" },
  { before: "/images/sofa-before.jpg", after: "/images/sofa.webp", label: "Outdoor-Sofa", note: "Freisteller → Lifestyle-Inszenierung" },
  { before: "/images/dress-studio.jpg", after: "/images/dress-beach.jpg", label: "Abendkleid", note: "Studio → Kampagnen-Look", pos: "object-[50%_15%]" },
];

const Slider = ({ pair, index }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)));
  }, []);

  const onDown = (e) => { dragging.current = true; update(e.clientX); };
  const onMove = (e) => { if (dragging.current) update(e.clientX); };
  const stop = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={stop}
      onPointerLeave={stop}
      className="relative aspect-square select-none cursor-ew-resize overflow-hidden border border-[var(--line)] touch-pan-y"
      data-testid={`compare-slider-${index}`}
      data-lenis-prevent
    >
      <img src={pair.after} alt={`${pair.label} – nachher`} className={`absolute inset-0 w-full h-full object-cover ${pair.pos || ""}`} draggable={false} />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={pair.before} alt={`${pair.label} – vorher`} className={`absolute inset-0 h-full object-cover ${pair.pos || ""}`} style={{ width: ref.current?.offsetWidth || "100%", maxWidth: "none" }} draggable={false} />
      </div>
      <div className="absolute top-0 bottom-0 w-px bg-[var(--gold)]" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[var(--gold)] text-[#0a0a0b] flex items-center justify-center shadow-[0_0_0_6px_rgba(212,175,55,0.2)]" data-testid={`compare-handle-${index}`}>
          <ChevronsLeftRight size={18} />
        </div>
      </div>
      <span className="absolute top-4 left-4 bg-[#0a0a0b]/80 text-[var(--muted)] font-mono text-[0.6rem] tracking-[0.25em] uppercase px-2.5 py-1.5">Vorher</span>
      <span className="absolute top-4 right-4 bg-[var(--gold)] text-[#0a0a0b] font-mono text-[0.6rem] tracking-[0.25em] uppercase px-2.5 py-1.5">Nachher</span>
      <input
        type="range" min="0" max="100" value={Math.round(pos)} onChange={(e) => setPos(Number(e.target.value))}
        aria-label={`Vergleich ${pair.label}`} className="absolute inset-x-0 bottom-0 opacity-0 h-10 cursor-ew-resize"
        data-testid={`compare-range-${index}`}
      />
    </div>
  );
};

export const BeforeAfter = () => (
  <section id="vergleich" className="relative py-28 lg:py-40 bg-[var(--bg-2)]" data-testid="before-after-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <SectionHead
        eyebrow="Vorher / Nachher"
        lines={["Vom Herstellerbild", "zur Inszenierung."]}
        text="Ziehen Sie den Regler und sehen Sie den Unterschied: links das schlichte Produktfoto, rechts die individuelle KI-Visualisierung."
      />
      <div className="grid md:grid-cols-3 gap-5" data-testid="before-after-grid">
        {PAIRS.map((p, i) => (
          <Reveal key={p.label} delay={i * 0.12} y={30}>
            <Slider pair={p} index={i} />
            <motion.div className="mt-4 flex items-start justify-between">
              <div>
                <p className="font-display text-xl">{p.label}</p>
                <p className="text-xs text-[var(--muted)] mt-1">{p.note}</p>
              </div>
              <span className="font-mono text-[0.6rem] text-[var(--gold)] mt-1">0{i + 1}</span>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
